import React, { useEffect, useMemo, useState } from 'react';
import { FaUserShield } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

type TenantClient = {
  id: string;
  name: string;
};

type RoleGroup = {
  id: number;
  name: string;
  user_count?: number;
  permissions?: string[];
};

const DEFAULT_ROLES = [
  'Client Manager',
  'Practice Manager',
  'Billing Manager',
  'Clinical Staff',
  'Prescriber',
  'Receptionist',
];

const parseRoleName = (groupName: string) => {
  if (!groupName.includes('::')) {
    return { clientId: '', roleName: groupName };
  }
  const [clientId, roleName] = groupName.split('::', 2);
  return { clientId, roleName };
};

const ClientRolesAdmin: React.FC = () => {
  const { token } = useAuth();

  const [clients, setClients] = useState<TenantClient[]>([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [groups, setGroups] = useState<RoleGroup[]>([]);
  const [newRoleName, setNewRoleName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadClients = async () => {
    if (!token) return;

    const response = await apiCall('/core/tenant-context/', { method: 'GET' }, token);
    if (!response.ok) {
      return;
    }

    const data = await response.json();
    const items = Array.isArray(data.clients)
      ? data.clients.map((client: any) => ({
          id: String(client.id || ''),
          name: String(client.name || client.id || ''),
        }))
      : [];

    setClients(items);
  };

  const loadGroups = async (clientId: string) => {
    if (!token) return;

    setLoading(true);
    setError('');

    try {
      const response = await apiCall(`/authentication/groups/?client_id=${encodeURIComponent(clientId)}`, { method: 'GET' }, token);
      if (!response.ok) {
        throw new Error(`Failed to load roles: HTTP ${response.status}`);
      }

      const data = await response.json();
      const items = Array.isArray(data) ? data : Array.isArray(data.results) ? data.results : [];
      setGroups(items);
    } catch (e: any) {
      setError(e.message || 'Failed to load roles');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadClients();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadGroups(selectedClient);
  }, [token, selectedClient]);

  const clientNameById = useMemo(() => {
    const map = new Map<string, string>();
    clients.forEach((client) => map.set(client.id.toLowerCase(), client.name));
    return map;
  }, [clients]);

  const createRole = async () => {
    if (!token) return;
    if (selectedClient === 'all') {
      setError('Select a specific client to create a role.');
      return;
    }

    const trimmed = newRoleName.trim();
    if (!trimmed) {
      setError('Role name is required.');
      return;
    }

    const scopedName = `${selectedClient}::${trimmed}`;

    setSaving(true);
    setError('');
    try {
      const response = await apiCall('/authentication/groups/', {
        method: 'POST',
        body: JSON.stringify({ name: scopedName }),
      }, token);

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.name?.[0] || `Failed to create role: HTTP ${response.status}`);
      }

      setNewRoleName('');
      await loadGroups(selectedClient);
    } catch (e: any) {
      setError(e.message || 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async (groupId: number) => {
    if (!token) return;

    setSaving(true);
    setError('');
    try {
      const response = await apiCall(`/authentication/groups/${groupId}/`, { method: 'DELETE' }, token);
      if (!response.ok && response.status !== 204) {
        throw new Error(`Failed to delete role: HTTP ${response.status}`);
      }
      await loadGroups(selectedClient);
    } catch (e: any) {
      setError(e.message || 'Failed to delete role');
    } finally {
      setSaving(false);
    }
  };

  const defaultRoleSet = new Set(DEFAULT_ROLES.map((role) => role.toLowerCase()));

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-base-content flex items-center gap-3">
          <FaUserShield className="text-primary" />
          Roles
        </h1>
      </div>

      <div className="card bg-base-100 border border-base-300 shadow-sm">
        <div className="card-body space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <label className="form-control w-full">
              <span className="label"><span className="label-text font-semibold">Client</span></span>
              <select
                className="select select-bordered"
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
              >
                <option value="all">All</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </label>

            <label className="form-control w-full lg:col-span-2">
              <span className="label"><span className="label-text font-semibold">Create Role</span></span>
              <div className="join w-full">
                <input
                  className="input input-bordered join-item w-full"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder={selectedClient === 'all' ? 'Select a specific client first' : 'e.g. Theatre Coordinator'}
                  disabled={selectedClient === 'all' || saving}
                />
                <button className="btn btn-primary join-item" onClick={createRole} disabled={selectedClient === 'all' || saving}>
                  Add Role
                </button>
              </div>
            </label>
          </div>

          {error && <div className="alert alert-error"><span>{error}</span></div>}

          <div className="overflow-x-auto">
            <table className="table table-zebra table-fixed w-full">
              <thead>
                <tr>
                  <th className="w-[28%]">Role</th>
                  <th className="w-[28%]">Client</th>
                  <th className="w-[12%]">Users</th>
                  <th className="w-[18%]">Default</th>
                  <th className="w-[14%]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center">Loading roles...</td></tr>
                ) : groups.length === 0 ? (
                  <tr><td colSpan={5} className="text-center">No roles found.</td></tr>
                ) : (
                  groups.map((group) => {
                    const parsed = parseRoleName(group.name);
                    const clientLabel = parsed.clientId ? (clientNameById.get(parsed.clientId.toLowerCase()) || parsed.clientId) : 'Global';
                    const isDefault = defaultRoleSet.has(parsed.roleName.toLowerCase());

                    return (
                      <tr key={group.id}>
                        <td>{parsed.roleName}</td>
                        <td>{clientLabel}</td>
                        <td>{group.user_count ?? 0}</td>
                        <td>{isDefault ? <span className="badge badge-info badge-sm">Default</span> : '-'}</td>
                        <td>
                          <button
                            className="btn btn-xs btn-outline btn-error"
                            onClick={() => deleteRole(group.id)}
                            disabled={saving}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientRolesAdmin;
